import React, { useState, useEffect, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

interface MonthSliderProps {
  selectedMonth: number;
  selectedYear: number;
  onMonthSelect: (month: number, year: number) => void;
  monthlyTotals: Array<{ month: number; completed_count: number }>;
}

type Month = {
  month: number;
  year: number;
};

const MonthSlider = ({ 
  selectedMonth, 
  selectedYear, 
  onMonthSelect, 
  monthlyTotals 
}: MonthSliderProps) => {
  const { t } = useI18n();
  const [visibleMonths, setVisibleMonths] = useState<Month[]>([]);
  
  const monthNames = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];

  useEffect(() => {
    // Generate visible months (current year and adjacent years)
    const months = [];
    const currentYear = new Date().getFullYear();
    
    // Add previous year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear - 1 });
    }
    
    // Add current year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear });
    }
    
    // Add next year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear + 1 });
    }
    
    setVisibleMonths(months);
  }, []); // Only run once on mount

  const getTrainingCount = (month: number, year: number): number => {
    // monthlyTotals is ordered: [prev year months 1-12, current year months 1-12, next year months 1-12]
    // Total of 36 items (12 + 12 + 12)
    const currentYear = new Date().getFullYear();
    let monthIndex = -1;
    
    if (year === currentYear - 1) {
      // Previous year: months 1-12 are at indices 0-11
      monthIndex = month - 1;
    } else if (year === currentYear) {
      // Current year: months 1-12 are at indices 12-23
      monthIndex = 12 + (month - 1);
    } else if (year === currentYear + 1) {
      // Next year: months 1-12 are at indices 24-35
      monthIndex = 24 + (month - 1);
    }
    
    // Ensure we have exactly 36 items, and the index is valid
    if (monthlyTotals.length === 36 && monthIndex >= 0 && monthIndex < monthlyTotals.length) {
      const monthData = monthlyTotals[monthIndex];
      // Verify the month matches (safety check - data should be in correct order)
      if (monthData && monthData.month === month) {
        return monthData.completed_count;
      }
    }
    
    return 0;
  };

  const scrollToSelectedMonth = useCallback(() => {
    // Early return if not in browser or no months
    if (typeof window === 'undefined' || visibleMonths.length === 0) {
      return;
    }
    
    const selectedIndex = visibleMonths.findIndex(
      m => m.month === selectedMonth && m.year === selectedYear
    );
    
    if (selectedIndex !== -1) {
      const container = document.getElementById('month-slider-container');
      const item = document.getElementById(`month-item-${selectedIndex}`);
      
      if (container && item && container.offsetWidth > 0 && item.offsetWidth > 0) {
        const containerWidth = container.offsetWidth;
        const itemWidth = item.offsetWidth;
        const scrollLeft = item.offsetLeft - (containerWidth / 2) + (itemWidth / 2);
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [visibleMonths, selectedMonth, selectedYear]);

  // Scroll to selected month when it changes or when component mounts
  useEffect(() => {
    // Only scroll in browser environment (skip during tests)
    if (typeof window === 'undefined') {
      return;
    }

    // Use a small delay to ensure DOM is ready and visibleMonths are set
    const timeoutId = setTimeout(() => {
      scrollToSelectedMonth();
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [scrollToSelectedMonth, visibleMonths.length]);

  return (
    <div className="w-full mb-4">
      <div 
        id="month-slider-container"
        data-testid="month-slider-container"
        className="flex gap-3 overflow-x-auto scrollbar-hide px-2 py-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleMonths.map(({ month, year }, index) => {
          const isSelected = month === selectedMonth && year === selectedYear;
          const trainingCount = getTrainingCount(month, year);
          
          return (
            <div
              key={`${year}-${month}`}
              id={`month-item-${index}`}
              className={`flex flex-col items-center min-w-[50px] cursor-pointer transition-all duration-200 ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => onMonthSelect(month, year)}
            >
              {/* Month initials */}
              <span className={`text-xs font-medium ${
                isSelected ? 'text-white' : 'text-white/60'
              }`}>
                {t(`calendar.months.${monthNames[month - 1]}`)}
              </span>
              
              {/* Training count with icon */}
              <div className={`flex items-center gap-1 ${
                isSelected ? 'text-white' : 'text-white/40'
              }`}>
                <span className="text-[10px] font-medium">
                  {trainingCount.toString().padStart(2, '0')}
                </span>
                <Calendar className="w-2.5 h-2.5" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { MonthSlider };